import React, { useCallback, useEffect, useState } from 'react'
import Loading from 'components/center-loading'
import SnackBar from 'material-ui/Snackbar'
import intl from 'react-intl-universal'
import { loadGeneralSettings, loadBusinessUnitFeatureFlagDefinitions, saveGeneralSettings, loadBusinessUnitFeatureFlags, saveBusinessUnitFeatureFlag, removeBusinessUnitFeatureFlag } from './general-settings.service'
import { formatErrorMessage } from 'utils/handle-error'
import { updateGeneralSetting } from '../../../../services/general-settings/general-settings'
import FeatureFlagsSection from './components/featureFlagsSection'
import Tabs from 'components/tabs'
import * as S from './styled'
import { Row, Col } from "react-flexbox-grid";
import Button from 'components/button'
import { getFeatureFlagOptions } from './feature-flags.config'
import { SelectBox, Toggle, Input, Table, TableBody, TableHeader, TableHeaderColumn, TableRow, TableRowColumn } from 'components/uikit-adapter'

const isBooleanSetting = value => ["true", "false"].includes(String(value).toLowerCase())
const parseGeneralSettingValue = value => {
  if (String(value).toLowerCase() === "true") return true
  if (String(value).toLowerCase() === "false") return false
  if (String(value).trim() !== "" && !Number.isNaN(Number(value))) return Number(value)
  return value
}

const updateFlag = (flag, center, selection) => flag.EccPlant === center
  ? { ...flag, selection, dirty: true }
  : flag

const updateFlagSection = (section, parameter, center, selection) => section.definition.parameter === parameter
  ? { ...section, flags: section.flags.map(flag => updateFlag(flag, center, selection)) }
  : section

const updateFeatureFlagSelection = (sections, parameter, center, selection) =>
  sections.map(section => updateFlagSection(section, parameter, center, selection))

const GeneralSettings = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSavingFeatureFlags, setIsSavingFeatureFlags] = useState(false)
  const [feedback, setFeedback] = useState({
    messageFeedback: "",
    showFeedback: false
  })
  const [featureFlags, setFeatureFlags] = useState([])
  const [featureFlagDefinitions, setFeatureFlagDefinitions] = useState([])
  const [selectedFeatureFlags, setSelectedFeatureFlags] = useState([])
  const [selectedPlants, setSelectedPlants] = useState([])

  const [generalSettings, setGeneralSettings] = useState([])
  const [selectedGeneralSettings, setSelectedGeneralSettings] = useState([])

  const updateFeatureFlag = (parameter, center, selection) => {
    setFeatureFlags(previous => updateFeatureFlagSelection(previous, parameter, center, selection))
  }

  const updateGeneralSettingValue = (parameter, value) => {
    setGeneralSettings(prevState => prevState.map(setting => setting.Parametro === parameter
      ? { ...setting, Valor: String(value) }
      : setting))
  }

  const openFeedback = useCallback(message => {
    setFeedback({
      messageFeedback: message,
      showFeedback: true
    });
  }, [])

  const closeFeedback = () => {
    setFeedback({
      messageFeedback: "",
      showFeedback: false
    });
  }

  const processError = useCallback(e => {
    const errorMessage = formatErrorMessage(e)
    openFeedback(errorMessage)
  }, [openFeedback])

  const save = async () => {
    setIsLoading(true)
    const data = generalSettings.map(({ Parametro, Valor }) => ({ Parametro, Valor }))

    try {
      await saveGeneralSettings(data)
      data.forEach(setting => {
        updateGeneralSetting(setting.Parametro, parseGeneralSettingValue(setting.Valor))
      })
      openFeedback(intl.get("feedbacks.savedRegister"))
    } catch (err) {
      processError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const saveFeatureFlags = async () => {
    try {
      setIsSavingFeatureFlags(true)
      setIsLoading(true)
      await Promise.all(featureFlags.flatMap(section => section.flags.filter(flag => flag.dirty).map(flag =>
        flag.selection === '__DEFAULT__'
          ? removeBusinessUnitFeatureFlag(flag.EccPlant, section.definition.parameter)
          : saveBusinessUnitFeatureFlag(flag.EccPlant, section.definition.parameter, flag.selection)
      )))
      await initFeatureFlags()
      openFeedback(intl.get("feedbacks.savedRegister"))
    } catch (err) {
      processError(err)
    } finally {
      setIsSavingFeatureFlags(false)
      setIsLoading(false)
    }
  }

  const initConfigs = useCallback(async () => {
    try {
      setIsLoading(true)
      const settings = await loadGeneralSettings()
      setGeneralSettings(settings?.data || [])
    } catch (err) {
      processError(err)
    } finally {
      setIsLoading(false)
    }
  }, [processError])

  const initFeatureFlags = useCallback(async () => {
    const definitionsResponse = await loadBusinessUnitFeatureFlagDefinitions()
    const definitions = (definitionsResponse.data || []).map(definition => ({
      parameter: definition.Parameter,
      title: definition.Parameter,
      description: definition.Description,
      control: definition.Control,
      options: (definition.AllowedValues || []).map(value => ({ value, label: value }))
    }))
    setFeatureFlagDefinitions(definitions)
    setSelectedFeatureFlags(previous => previous.length ? previous : definitions.slice(0, 1).map(definition => definition.parameter))

    const sections = await Promise.all(definitions.map(async definition => {
      const response = await loadBusinessUnitFeatureFlags(definition.parameter)
      const flags = (response.data || []).map(flag => ({
        ...flag,
        options: getFeatureFlagOptions(definition, flag.DefaultValue),
        selection: flag.HasOverride ? flag.Value : '__DEFAULT__',
        dirty: false
      }))
      return { definition, flags }
    }))
    setFeatureFlags(sections)
  }, [])

  useEffect(() => {
    initFeatureFlags().catch(processError)
    initConfigs()
  }, [initFeatureFlags, initConfigs, processError])

  const generalFlagsContent = (
    <S.FeatureFlagsSection data-testid="general-flags-section">
      <S.FeatureFlagsHeader>
        <S.FeatureFlagsTitleGroup>
          <S.FeatureFlagsTitle>{intl.get("master-data.configurations.general-settings.feature-flags.generalTitle")}</S.FeatureFlagsTitle>
          <S.FeatureFlagsSubtitle>{intl.get("master-data.configurations.general-settings.feature-flags.generalSubtitle")}</S.FeatureFlagsSubtitle>
        </S.FeatureFlagsTitleGroup>
        <S.FeatureFlagsSummary>{generalSettings.length} {intl.get("master-data.configurations.general-settings.feature-flags.generalSettingsCount")}</S.FeatureFlagsSummary>
      </S.FeatureFlagsHeader>
      <SelectBox
        label={intl.get("master-data.configurations.general-settings.feature-flags.flagsLabel")}
        placeholder={intl.get("master-data.configurations.general-settings.feature-flags.flagsPlaceholder")}
        options={generalSettings.map(setting => ({
          value: setting.Parametro,
          label: `${setting.Parametro} - ${setting.EnumString || ""}`
        }))}
        value={generalSettings
          .filter(setting => selectedGeneralSettings.includes(setting.Parametro))
          .map(setting => ({
            value: setting.Parametro,
            label: `${setting.Parametro} - ${setting.EnumString || ""}`
          }))}
        valueKey="value"
        labelKey="label"
        multi
        searchable
        clearable
        onChange={options => setSelectedGeneralSettings((options || []).map(option => option.value))}
        name="general-flags-filter"
      />
      <Table selectable={false} multiSelectable={false}>
        <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
          <TableRow>
            <TableHeaderColumn style={{ width: "8%" }}>{intl.get("master-data.configurations.general-settings.feature-flags.id")}</TableHeaderColumn>
            <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.enumString")}</TableHeaderColumn>
            <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.description")}</TableHeaderColumn>
            <TableHeaderColumn>{intl.get("master-data.configurations.general-settings.feature-flags.value")}</TableHeaderColumn>
          </TableRow>
        </TableHeader>
        <TableBody displayRowCheckbox={false}>
          {generalSettings.filter(setting => selectedGeneralSettings.length === 0 || selectedGeneralSettings.includes(setting.Parametro)).map(setting => (
            <TableRow key={setting.Parametro} data-testid={`general-setting-row-${setting.Parametro}`}>
              <TableRowColumn style={{ width: "8%" }}>
                <strong>{setting.Parametro}</strong>
              </TableRowColumn>
              <TableRowColumn>
                {setting.EnumString || ""}
              </TableRowColumn>
              <TableRowColumn>
                {setting.Descricao || ""}
              </TableRowColumn>
              <TableRowColumn>
                {isBooleanSetting(setting.Valor) ? (
                  <Toggle
                    label=""
                    name={`general-setting-${setting.Parametro}`}
                    value={String(setting.Valor).toLowerCase() === "true"}
                    onChange={target => updateGeneralSettingValue(setting.Parametro, target.checked ? "true" : "false")}
                  />
                ) : (
                  <Input
                    name={`general-setting-${setting.Parametro}`}
                    value={setting.Valor || ""}
                    onChange={event => updateGeneralSettingValue(setting.Parametro, event.target.value)}
                  />
                )}
              </TableRowColumn>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <S.FeatureFlagsFooter>
        <span className="helper-text">{intl.get("master-data.configurations.general-settings.feature-flags.saveGeneral")}</span>
        <Button buttonType='primary' value={intl.get(`master-data.configurations.general-settings.header.button`)} style={{ width: "152px" }} onClick={save} />
      </S.FeatureFlagsFooter>
    </S.FeatureFlagsSection>
  )

  const multiTenantFlagsContent = (
    <FeatureFlagsSection
      sections={featureFlags}
      definitions={featureFlagDefinitions}
      selectedFlags={selectedFeatureFlags}
      selectedPlants={selectedPlants}
      onFlagsChange={setSelectedFeatureFlags}
      onPlantsChange={setSelectedPlants}
      onToggle={updateFeatureFlag}
      onSave={saveFeatureFlags}
      isSaving={isSavingFeatureFlags}
    />
  )

  return (
    <React.Fragment>
      <Loading isLoading={isLoading} />
      <S.GeneralSettingsContent>
        <Row>
          <Col sm={12}>
            <Tabs
              selected={1}
              tabsContent={[
                { key: 1, label: intl.get("master-data.configurations.general-settings.feature-flags.generalTitle"), content: generalFlagsContent },
                { key: 2, label: intl.get("master-data.configurations.general-settings.feature-flags.multiTenantTitle"), content: multiTenantFlagsContent }
              ]}
            />
            <SnackBar
              message={feedback.messageFeedback}
              open={feedback.showFeedback}
              autoHideDuration={3000}
              onRequestClose={closeFeedback}
            />
          </Col>
        </Row>
      </S.GeneralSettingsContent>
    </React.Fragment>
  )
}

export default GeneralSettings
